import { runtimeEnv } from '../../config/runtimeEnv';
import { createTrustedScriptUrl } from '../../utils/trustedScriptUrl';
import { installAnalyticsTrustedScriptPolicy } from './AnalyticsTrustedScriptPolicy';
import type { AnalyticsProvider, AnalyticsUser, AnalyticsUserProperties } from './AnalyticsProvider';

type LinkedInInsightWindow = Window & {
  _linkedin_data_partner_ids?: string[];
  _linkedin_partner_id?: string;
};

const LINKEDIN_INSIGHT_SCRIPT_ID = 'linkedin-insight-script';
const LINKEDIN_INSIGHT_SCRIPT_URL = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
const LINKEDIN_INSIGHT_TRUSTED_TYPES_POLICY = 'linkedin-insight-script-loader';

export class LinkedInInsightAnalyticsProvider implements AnalyticsProvider {
  private initialized = false;

  init(): void {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }

    const partnerId = this.partnerId();
    if (!partnerId) {
      return;
    }

    this.initialized = true;
    installAnalyticsTrustedScriptPolicy();
    this.registerPartnerId(partnerId);
    this.loadScript();
  }

  identifyUser(user: AnalyticsUser | null): void {
    void user;
    // LinkedIn Insight is anonymous page/conversion tracking. User identity stays in PostHog/server analytics.
  }

  setUserProperties(properties: AnalyticsUserProperties): void {
    void properties;
    // LinkedIn Insight remains anonymous and must not receive user profile data.
  }

  reset(): void {
    this.initialized = false;
  }

  private partnerId(): string | null {
    const partnerId = String(runtimeEnv.VITE_LINKEDIN_INSIGHT_PARTNER_ID || '').trim();
    return /^\d+$/.test(partnerId) ? partnerId : null;
  }

  private registerPartnerId(partnerId: string): void {
    const browserWindow = window as LinkedInInsightWindow;
    browserWindow._linkedin_partner_id = partnerId;
    browserWindow._linkedin_data_partner_ids = browserWindow._linkedin_data_partner_ids || [];

    if (!browserWindow._linkedin_data_partner_ids.includes(partnerId)) {
      browserWindow._linkedin_data_partner_ids.push(partnerId);
    }
  }

  private loadScript(): void {
    if (document.getElementById(LINKEDIN_INSIGHT_SCRIPT_ID)) {
      return;
    }

    const script = document.createElement('script');
    script.id = LINKEDIN_INSIGHT_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = createTrustedScriptUrl(
      LINKEDIN_INSIGHT_TRUSTED_TYPES_POLICY,
      LINKEDIN_INSIGHT_SCRIPT_URL,
      this.validateScriptUrl,
    ) as string;
    document.head.appendChild(script);
  }

  private validateScriptUrl(url: string): string {
    const scriptUrl = new URL(url);
    if (
      scriptUrl.origin !== 'https://snap.licdn.com'
      || scriptUrl.pathname !== '/li.lms-analytics/insight.min.js'
    ) {
      throw new TypeError(`Untrusted LinkedIn Insight script URL: ${scriptUrl.toString()}`);
    }

    return scriptUrl.toString();
  }
}
